import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Trophy, GraduationCap, Users, ArrowRight, Check, Star, 
  Zap, Shield, Clock, ChevronDown, Menu, X, Send, Headphones, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL (versión actualizada)
const LOGO_URL = "/kinosis-logo.png";

export default function HomePage() {
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  
  // Modales de contacto
  const [showContactoVentas, setShowContactoVentas] = useState(false);
  const [showSoporte, setShowSoporte] = useState(false);
  const [enviandoForm, setEnviandoForm] = useState(false);
  
  // Formulario de contacto
  const [contactoForm, setContactoForm] = useState({
    nombre: '', email: '', mensaje: '', captcha: ''
  });
  
  // Formulario de soporte
  const [soporteForm, setSoporteForm] = useState({
    nombre: '', email: '', razon: '', mensaje: '', captcha: ''
  });
  
  // Captcha simple
  const [captchaQuestion, setCaptchaQuestion] = useState({ a: 0, b: 0 });
  
  const generateCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ a, b });
  };
  
  const handleOpenContacto = () => {
    generateCaptcha();
    setContactoForm({ nombre: '', email: '', mensaje: '', captcha: '' });
    setShowContactoVentas(true);
  };
  
  const handleOpenSoporte = () => {
    generateCaptcha();
    setSoporteForm({ nombre: '', email: '', razon: '', mensaje: '', captcha: '' });
    setShowSoporte(true);
  };
  
  const handleEnviarContacto = async () => {
    if (!contactoForm.nombre || !contactoForm.email || !contactoForm.mensaje) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    if (parseInt(contactoForm.captcha) !== captchaQuestion.a + captchaQuestion.b) {
      toast.error('La respuesta del captcha es incorrecta');
      return;
    }
    
    setEnviandoForm(true);
    try {
      await axios.post(`${API}/contacto/ventas`, {
        nombre: contactoForm.nombre,
        email: contactoForm.email,
        mensaje: contactoForm.mensaje,
        tipo: 'contacto'
      });
      toast.success('¡Mensaje enviado! Te contactaremos pronto.');
      setShowContactoVentas(false);
    } catch (error) {
      toast.error('Error al enviar el mensaje. Intenta nuevamente.');
    } finally {
      setEnviandoForm(false);
    }
  };
  
  const handleEnviarSoporte = async () => {
    if (!soporteForm.nombre || !soporteForm.email || !soporteForm.mensaje || !soporteForm.razon) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    if (parseInt(soporteForm.captcha) !== captchaQuestion.a + captchaQuestion.b) {
      toast.error('La respuesta del captcha es incorrecta');
      return;
    }
    
    setEnviandoForm(true);
    try {
      await axios.post(`${API}/contacto/soporte`, {
        nombre: soporteForm.nombre,
        email: soporteForm.email,
        mensaje: soporteForm.mensaje,
        razon_soporte: soporteForm.razon,
        tipo: 'soporte'
      });
      toast.success('¡Solicitud enviada! Te responderemos pronto.');
      setShowSoporte(false);
    } catch (error) {
      toast.error('Error al enviar la solicitud. Intenta nuevamente.');
    } finally {
      setEnviandoForm(false);
    }
  };

  const modules = [
    {
      id: 'ligas',
      title: 'Ligas Deportivas',
      description: 'Gestiona torneos, partidos, equipos y resultados en tiempo real',
      icon: Trophy,
      gradient: 'from-blue-600 to-cyan-500',
      bgGlow: 'bg-blue-500/20',
      path: '/ligas',
      comingSoon: true,
      price: null, // Próximamente
      priceCLP: '$39.990',
      priceUSD: '$49 USD',
      priceNote: 'por liga/mes',
      freeFeatures: [
        'Gestión básica de ligas',
        'Hasta 2 categorías',
        'Vista pública de resultados',
        'Máximo 50 equipos'
      ],
      proFeatures: [
        'Ligas y categorías ilimitadas',
        'Control de partidos en tiempo real',
        'Asignación de staff y canchas',
        'Estadísticas avanzadas',
        'Reportes y exportación',
        'Soporte prioritario'
      ]
    },
    {
      id: 'academias',
      title: 'Academias Deportivas',
      description: 'Administra clases, estudiantes, asistencia y pagos de tu academia',
      icon: GraduationCap,
      gradient: 'from-emerald-600 to-teal-500',
      bgGlow: 'bg-emerald-500/20',
      path: '/academias',
      comingSoon: false,
      priceCLP: '$19.990',
      priceUSD: '$23 USD',
      priceNote: 'por academia/mes',
      freeFeatures: [
        '1 categoría',
        'Hasta 20 alumnos',
        '1 entrenador',
        'Control de asistencia básico'
      ],
      proFeatures: [
        'Categorías y alumnos ilimitados',
        'Entrenadores ilimitados',
        'Gestión completa de clases',
        'Sistema de notas y asistencia',
        'Módulo de Finanzas y Contabilidad',
        'Soporte prioritario'
      ]
    },
    {
      id: 'club',
      title: 'Club Deportivo',
      description: 'Gestiona integrantes, categorías, finanzas y cuotas de tu club',
      icon: Users,
      gradient: 'from-purple-600 to-pink-500',
      bgGlow: 'bg-purple-500/20',
      path: '/club',
      comingSoon: false,
      priceCLP: '$4.990',
      priceUSD: '$7 USD',
      priceNote: 'por club/mes',
      freeFeatures: [
        '1 categoría',
        'Hasta 20 integrantes',
        'Planes de cuotas básicos',
        'Registro de pagos'
      ],
      proFeatures: [
        'Categorías ilimitadas',
        'Integrantes ilimitados',
        'Módulo de Contabilidad completo',
        'Pago automático por transferencia',
        'Reportes financieros',
        'Soporte prioritario'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
      {/* Decorative Background - Simplified */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-600/5 rounded-full blur-3xl" />
      </div>

      {/* Navbar */}
      <nav className="relative z-50 border-b border-slate-800/50 backdrop-blur-xl bg-slate-900/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Kinosis" className="h-16 w-auto" />
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#modulos" className="text-slate-300 hover:text-white transition-colors">Módulos</a>
              <a href="#planes" className="text-slate-300 hover:text-white transition-colors">Planes</a>
              <a href="#contacto" className="text-slate-300 hover:text-white transition-colors">Contacto</a>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-slate-300"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-slate-800 bg-slate-900/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-3">
              <a href="#modulos" className="block py-2 text-slate-300 hover:text-white">Módulos</a>
              <a href="#planes" className="block py-2 text-slate-300 hover:text-white">Planes</a>
              <a href="#contacto" className="block py-2 text-slate-300 hover:text-white">Contacto</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-6 bg-blue-600/20 text-white border-blue-500/30 px-4 py-1.5">
            <Zap className="w-4 h-4 mr-2" />
            Sistema de Gestión Deportiva Integral
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white">
            Gestiona tu organización
            <span className="block text-white">
              deportiva como un profesional
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10">
            Plataforma todo-en-uno para ligas, academias y clubes deportivos. 
            Simplifica tu administración y enfócate en lo que realmente importa: el deporte.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-lg px-8 py-6"
              onClick={() => document.getElementById('modulos')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Comenzar Gratis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-slate-700 text-slate-300 hover:bg-slate-800 text-lg px-8 py-6"
              onClick={() => document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Ver Planes
            </Button>
          </div>

          {/* Stats - Comentado hasta tener datos reales
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: '500+', label: 'Organizaciones' },
              { value: '10K+', label: 'Deportistas' },
              { value: '99.9%', label: 'Uptime' },
              { value: '24/7', label: 'Soporte' }
            ].map((stat, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-slate-400 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
          */}
        </div>
      </section>

      {/* Modules Section */}
      <section id="modulos" className="relative py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-600/20 text-white border-blue-500/30">
              Módulos Disponibles
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
              Elige tu módulo y comienza hoy
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Cada módulo está diseñado específicamente para las necesidades de tu tipo de organización deportiva
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <Card 
                  key={module.id}
                  className="relative overflow-hidden bg-slate-800/50 border-slate-700/50 backdrop-blur hover:border-slate-600 transition-all duration-300 group"
                >
                  {/* Glow effect */}
                  <div className={`absolute inset-0 ${module.bgGlow} opacity-0 group-hover:opacity-100 transition-opacity blur-3xl`} />
                  
                  <div className="relative p-6">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${module.gradient} flex items-center justify-center mb-5`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>

                    <h3 className="text-xl font-bold text-white mb-2">{module.title}</h3>
                    <p className="text-slate-400 text-sm mb-6">{module.description}</p>

                    {/* Features preview */}
                    <div className="space-y-2 mb-6">
                      {module.freeFeatures.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                          <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={() => !module.comingSoon && navigate(module.path)}
                      className={`w-full ${module.comingSoon 
                        ? 'bg-slate-600 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500'}`}
                      disabled={module.comingSoon}
                    >
                      {module.comingSoon ? (
                        <>
                          <Clock className="w-4 h-4 mr-2" />
                          Próximamente
                        </>
                      ) : (
                        <>
                          Acceder al Módulo
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planes" className="relative py-20 px-4 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-600/20 text-white border-blue-500/30">
              Planes y Precios
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
              Planes simples y transparentes
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Comienza gratis y actualiza cuando lo necesites. Sin sorpresas ni costos ocultos.
            </p>
          </div>

          {/* Plan Toggle */}
          <div className="flex justify-center gap-4 mb-12">
            <button
              onClick={() => setSelectedPlan('free')}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                selectedPlan === 'free' 
                  ? 'bg-slate-700 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Free
            </button>
            <button
              onClick={() => setSelectedPlan('pro')}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                selectedPlan === 'pro' 
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Star className="w-4 h-4" />
              Pro
            </button>
          </div>

          {/* Pricing Cards */}
          <div className="grid lg:grid-cols-3 gap-8">
            {modules.map((module) => {
              const Icon = module.icon;
              const isPro = selectedPlan === 'pro';
              const features = isPro ? module.proFeatures : module.freeFeatures;
              const isComingSoon = module.comingSoon;
              
              return (
                <Card 
                  key={module.id}
                  className={`relative overflow-hidden backdrop-blur transition-all duration-300 ${
                    isComingSoon
                      ? 'bg-slate-800/30 border-slate-700/30 opacity-80'
                      : isPro 
                        ? 'bg-gradient-to-b from-slate-800 to-slate-900 border-2 border-purple-500/50' 
                        : 'bg-slate-800/50 border-slate-700/50'
                  }`}
                >
                  {isComingSoon && (
                    <div className="absolute top-0 right-0 bg-slate-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      PRÓXIMAMENTE
                    </div>
                  )}
                  {!isComingSoon && isPro && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      RECOMENDADO
                    </div>
                  )}

                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${module.gradient} flex items-center justify-center ${isComingSoon ? 'opacity-60' : ''}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{module.title}</h3>
                        <p className="text-sm text-slate-400">{isPro ? 'Plan Pro' : 'Plan Free'}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      {isComingSoon ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-slate-400">Próximamente</span>
                        </div>
                      ) : isPro ? (
                        <div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold text-white">{module.priceCLP}</span>
                            <span className="text-slate-400">/mes</span>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            o {module.priceUSD}/mes • {module.priceNote}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-white">$0</span>
                          <span className="text-slate-400">Gratis siempre</span>
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <div className="space-y-3 mb-6">
                      {features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isPro ? 'bg-purple-500/20' : 'bg-slate-700'
                          }`}>
                            <Check className={`w-3 h-3 ${isPro ? 'text-purple-400' : 'text-emerald-400'}`} />
                          </div>
                          <span className="text-sm text-slate-300">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <Button
                      onClick={() => !isComingSoon && navigate(module.path)}
                      disabled={isComingSoon}
                      className={`w-full ${
                        isComingSoon
                          ? 'bg-slate-700 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500'
                      }`}
                    >
                      {isComingSoon ? (
                        <>
                          <Clock className="w-4 h-4 mr-2" />
                          Próximamente
                        </>
                      ) : 'Comenzar Gratis'}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center space-y-2">
            <p className="text-slate-400 text-sm">
              Todos los planes incluyen actualizaciones gratuitas y acceso a nuevas funcionalidades
            </p>
            <p className="text-slate-500 text-xs">
              * El precio es por unidad (1 club, 1 academia o 1 liga). Si necesitas múltiples, cada una requiere su propia licencia.
            </p>
            <p className="text-slate-500 text-xs">
              * Precios no incluyen IVA
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
              ¿Por qué elegir Kinosis?
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Diseñado por y para organizaciones deportivas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Zap, 
                title: 'Rápido y Moderno', 
                description: 'Interfaz intuitiva que funciona en cualquier dispositivo' 
              },
              { 
                icon: Shield, 
                title: 'Seguro', 
                description: 'Tus datos protegidos con los más altos estándares' 
              },
              { 
                icon: Clock, 
                title: 'Ahorra Tiempo', 
                description: 'Automatiza tareas repetitivas y enfócate en el deporte' 
              },
              { 
                icon: Star, 
                title: 'Soporte Experto', 
                description: 'Equipo dedicado para ayudarte cuando lo necesites' 
              }
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="p-6 bg-slate-800/50 border-slate-700/50 text-center">
                  <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-300 text-sm">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enrollment Section */}
      <section className="relative py-16 px-4 bg-slate-800/30">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 sm:p-10 bg-emerald-600/10 border-emerald-500/30 backdrop-blur">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-8 h-8 text-emerald-400" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2">Enrolamiento de Identidad</h3>
                <p className="text-slate-300 text-sm">
                  Verifica tu identidad una sola vez con reconocimiento facial para participar en ligas y clubes deportivos.
                  Proceso rapido, seguro y 100% digital.
                </p>
              </div>
              <Button
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                onClick={() => navigate('/enrolamiento')}
                data-testid="homepage-enrolamiento-btn"
              >
                Enrolarme Ahora
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contacto" className="relative py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 sm:p-12 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-blue-500/30 text-center backdrop-blur">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-white">
              ¿Listo para transformar tu gestión deportiva?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Únete a cientos de organizaciones que ya confían en Kinosis para su administración deportiva.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-slate-900 hover:bg-slate-100"
                onClick={() => document.getElementById('modulos')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Comenzar Ahora
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={handleOpenContacto}
              >
                Contactar Ventas
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Kinosis" className="h-14 w-auto" />
            </div>
            <div className="flex gap-6 text-sm text-slate-400">
              <a href="/terminos" className="hover:text-white transition-colors">Términos y Condiciones</a>
              <a href="/privacidad" className="hover:text-white transition-colors">Política de Privacidad</a>
              <button onClick={handleOpenSoporte} className="hover:text-white transition-colors">Soporte</button>
            </div>
            <p className="text-sm text-slate-500">
              © 2025 Kinosis. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Modal Contactar Ventas */}
      <Dialog open={showContactoVentas} onOpenChange={setShowContactoVentas}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-400" />
              Contactar a Ventas
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nombre *</Label>
              <Input
                value={contactoForm.nombre}
                onChange={(e) => setContactoForm({ ...contactoForm, nombre: e.target.value })}
                placeholder="Tu nombre"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={contactoForm.email}
                onChange={(e) => setContactoForm({ ...contactoForm, email: e.target.value })}
                placeholder="tu@email.com"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <div>
              <Label className="text-slate-300">Mensaje *</Label>
              <Textarea
                value={contactoForm.mensaje}
                onChange={(e) => setContactoForm({ ...contactoForm, mensaje: e.target.value })}
                placeholder="¿Cómo podemos ayudarte?"
                rows={4}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <div>
              <Label className="text-slate-300">
                Verificación: ¿Cuánto es {captchaQuestion.a} + {captchaQuestion.b}? *
              </Label>
              <Input
                value={contactoForm.captcha}
                onChange={(e) => setContactoForm({ ...contactoForm, captcha: e.target.value })}
                placeholder="Escribe el resultado"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <Button 
              onClick={handleEnviarContacto} 
              disabled={enviandoForm}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {enviandoForm ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Mensaje
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Soporte */}
      <Dialog open={showSoporte} onOpenChange={setShowSoporte}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headphones className="w-5 h-5 text-emerald-400" />
              Soporte Técnico
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nombre *</Label>
              <Input
                value={soporteForm.nombre}
                onChange={(e) => setSoporteForm({ ...soporteForm, nombre: e.target.value })}
                placeholder="Tu nombre"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={soporteForm.email}
                onChange={(e) => setSoporteForm({ ...soporteForm, email: e.target.value })}
                placeholder="tu@email.com"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <div>
              <Label className="text-slate-300">Motivo de consulta *</Label>
              <Select value={soporteForm.razon} onValueChange={(v) => setSoporteForm({ ...soporteForm, razon: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Selecciona una opción" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="dudas" className="text-white hover:bg-slate-600">Dudas del Producto</SelectItem>
                  <SelectItem value="error" className="text-white hover:bg-slate-600">Error en pantalla</SelectItem>
                  <SelectItem value="pago" className="text-white hover:bg-slate-600">Problema con pago</SelectItem>
                  <SelectItem value="acceso" className="text-white hover:bg-slate-600">No puedo acceder</SelectItem>
                  <SelectItem value="funcionalidad" className="text-white hover:bg-slate-600">Solicitar funcionalidad</SelectItem>
                  <SelectItem value="otro" className="text-white hover:bg-slate-600">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Descripción del problema *</Label>
              <Textarea
                value={soporteForm.mensaje}
                onChange={(e) => setSoporteForm({ ...soporteForm, mensaje: e.target.value })}
                placeholder="Describe tu problema o consulta en detalle"
                rows={4}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <div>
              <Label className="text-slate-300">
                Verificación: ¿Cuánto es {captchaQuestion.a} + {captchaQuestion.b}? *
              </Label>
              <Input
                value={soporteForm.captcha}
                onChange={(e) => setSoporteForm({ ...soporteForm, captcha: e.target.value })}
                placeholder="Escribe el resultado"
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              />
            </div>
            <Button 
              onClick={handleEnviarSoporte} 
              disabled={enviandoForm}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {enviandoForm ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Solicitud
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
